<script lang="ts">
	import { optimismTokensConfig } from '$lib/tokensConfig';
	import GradientBorder from '$lib/util/GradientBorder.svelte';
	import type { TokenSymbol } from '$lib/types/tokens';
	import { getDecimalOfToken, getNumBackInUnits, getPrecisionOfToken, toBN } from '$lib/util/util';
	import { userBalancesEnriched } from '$lib/home/store';
	import {
		addErrorNotification,
		addTransactionNotification
	} from '$lib/notifications/notificationManager';
	import { withdrawTokenFromSCW } from '$lib/web3/actions/scwModifiers';
	import { waitForTransaction, type SendTransactionResult } from '@wagmi/core';
	import { smartContractWalletAddress } from '$lib/home/scwallet/store';
	import ProcessingIcon from '$lib/util/ProcessingIcon.svelte';
	import { ClanIdToNameAndIconMap, addressToSendEntryFees } from './constants';
	import { getClanWarsSummary, getUserStatsInClanWars } from '$lib/data/clanWars';
	import { userStatsInClanWars } from './store';
	import { popupModalOpen } from '$lib/popups/popupsStore';
	import { goto } from '$app/navigation';
	import ShareOnXButton from '$lib/tradingSeason/shared/ShareOnXButton.svelte';
	import { handleTweetClick } from './clanHelpers';

	let selectedAsset: TokenSymbol = optimismTokensConfig.sUSD.symbol;
	let selectAssetDropdownOpen = false;
	let currentStep: 'SendToken' | 'AssigningClan' | 'ClanAssigned' = 'SendToken';
	let transferringToken = false;

	const entryFees = 10;

	const availableTokens: TokenSymbol[] = ['sUSD', 'USDCe', 'USDT'];

	$: isBalanceSufficient =
		parseFloat($userBalancesEnriched[selectedAsset]?.balance) >
		entryFees * 10 ** getDecimalOfToken(selectedAsset);

	$: if ($userBalancesEnriched) {
		availableTokens?.forEach((token) => {
			if (
				parseFloat($userBalancesEnriched[token]?.balance) >
				entryFees * 10 ** getDecimalOfToken(token)
			) {
				selectedAsset = token;
			}
		});
	}

	const executeTokenTransfer = async () => {
		try {
			if (!isBalanceSufficient || !$smartContractWalletAddress) return;

			transferringToken = true;
			let tx: SendTransactionResult = await withdrawTokenFromSCW(
				optimismTokensConfig[selectedAsset].address,
				$smartContractWalletAddress as `0x${string}`,
				toBN(entryFees.toString(), getDecimalOfToken(selectedAsset)),
				addressToSendEntryFees
			);
			currentStep = 'AssigningClan';
			addTransactionNotification(
				'Funds Withdrawn',
				`${entryFees} ${selectedAsset} has been transferred from Polynomial wallet as entry fees for clan wars.`,
				tx
			);

			waitForAssigningClan(tx);
		} catch (err: any) {
			console.log(err);
			addErrorNotification('Error', 'Something went wrong while sending the token.', err.message);
		} finally {
			transferringToken = false;
		}
	};

	const waitForAssigningClan = async (tx: SendTransactionResult) => {
		try {
			await waitForTransaction(tx);

			setTimeout(async () => {
				await getUserStatsInClanWars($smartContractWalletAddress);

				if ($userStatsInClanWars.clanId) {
					await getClanWarsSummary();
					currentStep = 'ClanAssigned';
				}
			}, 5000);
		} catch (err) {
			console.log(err);
		}
	};
</script>

<GradientBorder
	gradient="bg-fx-gradient-6"
	paddingClass="p-px"
	borderRadius="rounded-2xl"
	outerClassList=""
	innerClassList=""
>
	<div
		class="p-5 flex flex-col items-center justify-center gap-4 rounded-2xl bg-fx-gradient-8 w-[360px]"
	>
		{#if currentStep === 'SendToken'}
			<div class="w-full flex justify-start items-center gap-1.5">
				<img src="/clan-wars/sword.png" alt="Sword" loading="lazy" class="w-7 h-7" />
				<p class="text-sm font-semibold text-fx-gradient-1">Join a Clan</p>
			</div>
			<div class="h-px w-full rounded-xl bg-fx-gradient-6" />
			<div class="w-full flex flex-col items-start justify-center">
				<p class="text-sm text-fx-grey-300">
					To join a clan you need to deposit <span class="text-fx-blue">${entryFees}</span>.
				</p>
			</div>
			<div class="w-full flex flex-col items-start justify-center gap-2">
				<p class="text-xs text-fx-grey-200">Select Asset:</p>
				<div
					class="p-2 rounded-lg border border-fx-grey-600 bg-fx-black flex w-full items-center justify-between"
				>
					<div
						class="p-2 rounded-lg gap-1 flex justify-center items-center cursor-pointer bg-fx-grey-600"
						on:click={() => (selectAssetDropdownOpen = !selectAssetDropdownOpen)}
						role="presentation"
					>
						<img
							src={optimismTokensConfig[selectedAsset].logo}
							alt={selectedAsset}
							class="w-6 h-6"
							loading="lazy"
						/>
						<p class="text-sm font-medium text-fx-grey-200 -mb-1">
							{optimismTokensConfig[selectedAsset].symbol}
						</p>
						<img
							src="/down_arrow_bold.svg"
							alt="Down Arrow"
							class="h-2.5 -mb-0.5 {selectAssetDropdownOpen
								? 'rotate-180'
								: 'rotate-0'} transition-all duration-300"
							loading="lazy"
						/>
					</div>
					<p class="text-base text-fx-grey-400">${entryFees}</p>
				</div>
				<div
					class="gap-2 flex flex-col w-full items-center justify-center rounded-lg border-[0.5px] border-fx-grey-500 bg-fx-gradient-24 overflow-hidden transition-all duration-300 {!selectAssetDropdownOpen
						? 'max-h-0 p-0 opacity-0'
						: 'max-h-[150px] p-2 opacity-100'}"
				>
					{#each availableTokens as token}
						<div
							class="p-2 flex w-full items-center justify-between bg-fx-grey-700 rounded-md cursor-pointer hover:bg-fx-grey-500 duration-200"
							on:click={() => {
								selectedAsset = token;
								selectAssetDropdownOpen = false;
							}}
							role="presentation"
						>
							<div class="flex justify-start items-center gap-1">
								<img
									src={optimismTokensConfig[token].logo}
									alt={token}
									class="w-6 h-6"
									loading="lazy"
								/>
								<p class="text-sm font-medium text-fx-grey-200 -mb-1">
									{optimismTokensConfig[token].symbol}
								</p>
							</div>
							<div class="flex justify-end items-center gap-0.5">
								<p class="text-xs text-fx-grey-400">Balance:</p>
								<p class="text-xs font-semibold text-fx-blue">
									{getNumBackInUnits(
										$userBalancesEnriched[token]?.balance,
										getDecimalOfToken(token),
										getPrecisionOfToken(token)
									)}
								</p>
							</div>
						</div>
					{/each}
				</div>
			</div>
			<div class="px-3 py-4 rounded w-full bg-fx-grey-900 border-[0.5px] border-fx-grey-600">
				<p class="text-xs text-fx-gradient-9">You’ll be randomly assigned to one of the 4 clans.</p>
			</div>
			<div
				class="w-full flex justify-center items-center py-3 rounded {isBalanceSufficient
					? 'cursor-pointer bg-fx-gradient-1'
					: 'cursor-not-allowed bg-fx-grey-500'}"
				on:click={executeTokenTransfer}
				role="presentation"
			>
				{#if transferringToken}
					<ProcessingIcon />
				{:else}
					<p
						class="text-sm font-semibold {!isBalanceSufficient
							? 'text-fx-grey-200'
							: 'text-fx-grey-900'}"
					>
						{!isBalanceSufficient ? 'Insufficient Balance' : 'Confirm Deposit'}
					</p>
				{/if}
			</div>
		{:else if currentStep === 'AssigningClan'}
			<div class="w-full flex justify-start items-center gap-1.5">
				<img src="/clan-wars/sword.png" alt="Sword" loading="lazy" class="w-7 h-7" />
				<p class="text-sm font-semibold text-fx-gradient-1">Assigning a Clan</p>
			</div>
			<div class="h-px w-full rounded-xl bg-fx-gradient-6" />
			<img src="/clan-wars/swords-grey.webp" alt="Swords" class="w-[150px]" loading="lazy" />
			<p class="text-xs text-fx-gradient-9">You’re being randomly assigned to a clan.</p>
			<div
				class="w-full bg-fx-grey-600 flex justify-center items-center py-3 rounded cursor-not-allowed"
			>
				<ProcessingIcon />
			</div>
		{:else if currentStep === 'ClanAssigned'}
			<div class="w-full flex justify-start items-center gap-1.5">
				<img src="/clan-wars/sword.png" alt="Sword" loading="lazy" class="w-7 h-7" />
				<p class="text-sm font-semibold text-fx-gradient-1">Clan Assigned</p>
			</div>
			<div class="h-px w-full rounded-xl bg-fx-gradient-6" />
			<p class="text-xs text-fx-gradient-9">You’ve been assigned to a clan.</p>
			{#if $userStatsInClanWars?.clanId}
				<div
					class="w-full flex flex-col justify-center items-center z-10 py-2 gap-0 bg-fx-grey-600 rounded-md"
				>
					<img
						src={ClanIdToNameAndIconMap[$userStatsInClanWars.clanId]?.clanIcon}
						alt="Clan"
						class="w-[100px]"
					/>
					<p class="text-2xl font-semibold text-fx-grey-100">
						{ClanIdToNameAndIconMap[$userStatsInClanWars.clanId]?.name}
					</p>
				</div>
			{/if}

			<ShareOnXButton
				onClick={() =>
					handleTweetClick(
						$userStatsInClanWars?.clanId
							? ClanIdToNameAndIconMap[$userStatsInClanWars?.clanId]?.name
							: ''
					)}
			/>
			<div
				class="w-full bg-fx-gradient-1 flex justify-center items-center py-3 rounded cursor-pointer"
				on:click={() => {
					popupModalOpen.set(false);
					goto('/perps');
				}}
				role="presentation"
			>
				<p class="text-sm font-semibold text-fx-grey-900">Start Trading</p>
			</div>
		{/if}
	</div>
</GradientBorder>
